import User from "@/classes/User";
import sharedMethods from "@/sharedMethods";
import axios, { AxiosError } from "axios";
import jwt_decode from "jwt-decode";
import { ActionTree } from "vuex";
import router from "../../router/index";
import eventBus from "../../services/eventBus";
import { GlobalInterface } from "../index.js";

interface StateInterface { }

interface tokenInterface {
	expiresIn: Number;
	last_login: Date;
}

const actions: ActionTree<StateInterface, GlobalInterface> = {
	/**
	 * sets if first use tour should be showed or not
	 * @param showTour if tour should be showed or not
	 */
	setFirstUseState: ({ commit }: any, showTour: boolean) => {
		commit("updateFirstUseState", showTour);
	},

	async updateUserName(
		{ rootState, commit, getters },
		obj: { firstname: string; lastname: string; id: string }
	) {
		const response = await axios
			.put(rootState.api + "usermanagement/change-name", obj)
			.then(() => {
				const user = getters.getUser.clone();
				user.Vorname = obj.firstname;
				user.Nachname = obj.lastname;
				commit("setUser", user);
			})
			.catch((error) => {
				console.log("Update name error: " + error);
			});

		return response;
	},

	async updatePassword(
		{ rootState },
		obj: { oldPW: string; newPW: string; id?: string }
	) {
		obj.id = rootState.user.id;

		const response = await axios
			.post(rootState.api + "usermanagement/change-password", obj)
			.catch((error) => {
				console.log("Update password error: " + error);
			});

		return response;
	},

	/**
	 * update user settings (sets isNotificationReceiver)
	 * @param payload
	 * @returns response
	 */
	async setUserSettings({ rootState, commit }, payload: User) {
		const obj = {
			userId: payload.id,
			isNotificationReceiver: +payload.isNotificationReceiver,
		};

		const resp = await axios
			.put(rootState.api + "usermanagement/user-settings", obj)
			.then(() => {
				commit("setUserSettings", payload);
			})
			.catch((error) => {
				console.error("set user settings error: ", error);
			});

		return resp;
	},

	/**
	 * sets the facebook (instagram) token
	 * @param token token to set
	 */
	setFBToken: ({ commit }: any, token: string) => {
		commit("setFBToken", token);
	},

	/**
	 * logs the user in (just locally in store)
	 * sets the token, the user store object, and the fb token
	 */
	async login({ commit, rootState }, credentials: any) {
		const response = await axios.post(rootState.api + "login/", credentials);

		const token = response.data.user.token;
		const user = response.data.user;

		if (response.data.msg === "logged in") {
			commit("setToken", token);
			commit("setUser", User.fromObject(user));
			commit("setFBToken", user.instagramAccessToken);

			var decoded = jwt_decode<tokenInterface>(token);

			commit("setTokenExpire", decoded.expiresIn);

			if (!user.last_login) commit("updateFirstUseState", true);

			// set auth header
			axios.defaults.headers.common["Authorization"] = `${token}`;
			return "logged in";
		} else {
			sharedMethods.errorNotify(response.data.msg);
		}
		return "an error occured: " + response.data.msg;
	},

	/**
	 * register a new user with given credentials
	 * @param credentials
	 * @see https://api-dev.mirrads.de/api-docs/#/Usermanagement/post_api_usermanagement_sign_up
	 * @returns response
	 */
	async signUp({ rootState }, credentials) {
		const response = await axios.post(
			rootState.api + "usermanagement/sign-up",
			credentials
		);
		return response.data;
	},

	/**
	 * verify register token
	 * @param token
	 * @returns response
	 */
	async verifyToken({ rootState }, token) {
		const response = await axios.post(
			rootState.api + "usermanagement/sign-up",
			token
		);
		return response.data;
	},

	/**
	 * sets the user email to the given one
	 * @param payload must contain id, email
	 */
	async changeUserEmail({ commit, rootState }, email) {
		console.log("change Email");
		let payload = {
			email: email,
			id: rootState.user.id,
		};

		try {
			const response = await axios.post(
				rootState.api + "usermanagement/change-email",
				payload
			);

			if (response.data.status === 200) {
				commit("setUserEmail", payload.email);
				sharedMethods.successNotify("New Email set");
			}

			return response;
		} catch (e) {
			console.error("change user mail error:", e);
			sharedMethods.errorNotify(e.data.msg);
		}

		return null;
	},

	async postInstagramToken({ rootState, commit }, token) {
		const requestBody = {
			mrds_accId: rootState.user.AccID,
			client_id: "4071816732941874",
			client_secret: "b6c5d17fa9f337f32034d8f9aba5ef5b",
			code: token,
			grant_type: "authorization_code",
			redirect_uri: rootState.hostname + "/auth/",
		};

		axios
			.post(rootState.api + "usermanagement/instagram-token", requestBody)
			.then((response) => {
				commit("setFBToken", response.data.msg.access_token);
			});
	},

	/**
	 * updates the user profile image
	 */
	async updateUserIcon({ rootState }, file) {
		const formData = new FormData();
		formData.append("accid", rootState.user.AccID.toString());
		formData.append("file", file);

		await axios
			.post(rootState.api + "/mediamanagement/user-icon", formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			})
			.then(
				(response) => {
					if (response.status === 200) {
						return null;
					} else {
						return "There was an unknown error " + response.status;
					}
				},
				(error) => {
					console.log(error);
					return error;
				}
			);
	},
	/**
	 * calls the server to refresh the current token
	 * @returns response
	 */
	async refreshToken({ rootState, commit }) {
		return new Promise((resolve, reject) => {
			axios
				.get(rootState.api + "refresh-token")
				.then((response) => {
					var token = response.data.msg;

					commit("setToken", { token });
					resolve(response);
				})
				.catch((error) => {
					console.log("MDE GOT TOKEN ERROR" + error);
					commit("logout");
					resolve(error.response);
				});
		});
	},
	/**
	 * just a helper method for refreshToken
	 */
	refreshTokenLocally: ({ commit }: any, { token }: any) => {
		commit("setToken", token);
		var decoded = jwt_decode<tokenInterface>(token);

		commit("setTokenExpire", decoded.expiresIn);
		console.log("last login: " + decoded.last_login);

		// set auth header
		axios.defaults.headers.common["Authorization"] = `${token}`;
	},

	/**
	 * logs the user out (resets store, hides menu and redirects to login)
	 */
	logout: ({ commit }: any) => {
		commit("RESET", "");
		eventBus.$emit("hideElements");
		if (router.currentRoute.path !== "/login") {
			router.push("/login");
		}
	},

	/**
	 * fetches all enabled and disabled features of current user
	 * @returns response
	 */
	async fetchFeatureList({ commit, rootState }) {
		const id = rootState.user.id;

		const response = await axios.get(
			rootState.api + `usermanagement/get-feature-list?userId=${id}`
		);

		commit("setUserFeatureList", response.data.msg);

		return response.data;
	},
};

export default actions;
